import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { useFlashcardsStore } from "@/features/flashcards/store";
import { getNextReviewLabel, type ReviewRating, type SM2Fields } from "@/features/flashcards/sm2";
import { X, Lightbulb, ChevronLeft, ChevronRight, Check, Star, Pencil, Settings2 } from "lucide-react";
import { cn } from "@/lib/utils";

export function StudyModeScreen() {
  const {
    dueCards,
    studyIndex,
    exitStudyMode,
    rateCard,
    nextCard,
    previousCard,
    toggleStarById
  } = useFlashcardsStore();

  const [isFlipped, setIsFlipped] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [isRating, setIsRating] = useState(false);
  const [reviewedCount, setReviewedCount] = useState(0);
  const initialTotal = useRef(dueCards.length);

  const currentCard = dueCards[studyIndex];

  const handleRate = async (rating: ReviewRating) => {
    if (isRating) return;
    setIsRating(true);
    try {
      await rateCard(rating);
      setReviewedCount((c) => c + 1);
      setIsFlipped(false);
      setShowHint(false);
      setShowExplanation(false);
    } finally {
      setIsRating(false);
    }
  };

  const getSM2Fields = (): SM2Fields => ({
    ef: currentCard?.ef ?? 2.5,
    interval: currentCard?.interval ?? 0,
    repetitions: currentCard?.repetitions ?? 0,
    due_at: currentCard?.due_at ?? new Date().toISOString()
  });

  // Completion screen
  if (dueCards.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-6 p-6">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
          <Check className="h-10 w-10 text-green-600 dark:text-green-400" />
        </div>
        <div className="text-center">
          <h2 className="text-xl font-semibold">All cards reviewed!</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Great job! Come back later when more cards are due.
          </p>
        </div>
        <Button onClick={exitStudyMode} variant="default">
          Back to Deck
        </Button>
      </div>);

  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="mb-2 flex w-full items-center justify-between p-3 py-[4px]">
        <Button
          variant="ghost"
          size="sm"
          onClick={exitStudyMode}
          className="flex items-center gap-2 pl-1.5 text-muted-foreground rounded-lg">

          <ChevronLeft className="h-4 w-4 flex-shrink-0" />
          <span>Menu</span>
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8 border-none text-muted-foreground">

          <Settings2 className="h-4 w-4" />
          <span className="sr-only">Flashcards Preferences</span>
        </Button>
      </div>

      {/* Progress bar */}
      <div className="mb-2 flex items-center justify-center gap-2 px-4 pt-3 py-[6px]">
        <span className="text-sm text-muted-foreground">{reviewedCount}</span>
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={initialTotal.current > 0 ? Math.round(reviewedCount / initialTotal.current * 100) : 0}
          className="relative h-3 w-full overflow-hidden rounded-full bg-primary/5 dark:bg-primary/10 transition-all duration-300">

          <div
            className="h-full w-full flex-1 rounded-full bg-correct opacity-80 transition-all duration-300"
            style={{
              transform: `translateX(-${100 - (initialTotal.current > 0 ? reviewedCount / initialTotal.current * 100 : 0)}%)`
            }} />

        </div>
        <span className="text-sm text-muted-foreground">{initialTotal.current}</span>
      </div>

      {/* Card area */}
      <div className="flex flex-1 flex-col items-center justify-center p-4">
        <div className="w-full">
          {/* Flashcard with 3D flip */}
          <div className="w-full">
            <div className="perspective-1000">
              <div
                className={cn(
                  "relative aspect-[4/3] w-full cursor-pointer rounded-lg border text-card-foreground shadow-sm transition-all duration-500 preserve-3d bg-popover",
                  isFlipped && "rotate-y-180"
                )}
                onClick={() => setIsFlipped(!isFlipped)}>

                {/* Front */}
                <div className="backface-hidden absolute inset-0 flex h-full flex-col overflow-y-auto p-4 text-center bg-popover rounded-2xl">
                  <div className="flex items-start justify-between">
                    <div>
                      {currentCard.hint &&
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-9 px-3 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowHint(!showHint);
                        }}>

                          <Lightbulb className="mr-1 h-4 w-4" />
                          <span className="hidden text-sm font-normal lg:block">Hint</span>
                        </Button>
                      }
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn("h-9 w-9", currentCard.is_starred && "text-yellow-500")}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleStarById(currentCard.id);
                        }}>

                        <Star className={cn("h-4 w-4", currentCard.is_starred && "fill-yellow-400")} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9"
                        onClick={(e) => e.stopPropagation()}>

                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-grow flex-col items-center justify-center px-2">
                    <p className="max-w-xl text-base leading-7 lg:text-lg">
                      {currentCard.term || "Empty term"}
                    </p>
                    {showHint && currentCard.hint &&
                    <p className="mt-4 text-sm text-muted-foreground italic">
                        Hint: {currentCard.hint}
                      </p>
                    }
                  </div>
                </div>

                {/* Back */}
                <div className="backface-hidden rotate-y-180 absolute inset-0 flex h-full flex-col p-4 text-center">
                  <div className="flex items-start justify-end">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn("h-9 w-9", currentCard.is_starred && "text-yellow-500")}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleStarById(currentCard.id);
                        }}>

                        <Star className={cn("h-4 w-4", currentCard.is_starred && "fill-yellow-400")} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9"
                        onClick={(e) => e.stopPropagation()}>

                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-grow flex-col items-center justify-center overflow-y-auto px-2">
                    <p className="max-w-xl text-base leading-7 lg:text-lg">
                      {currentCard.definition || "Empty definition"}
                    </p>
                    {currentCard.explanation &&
                    <>
                        <Button
                        variant="ghost"
                        size="sm"
                        className="mt-3 h-8 px-3 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowExplanation((prev) => !prev);
                        }}>

                          {showExplanation ? "Hide explanation" : "Show explanation"}
                        </Button>
                        {showExplanation &&
                      <p className="mt-2 max-w-xl text-sm text-muted-foreground leading-6">
                            {currentCard.explanation}
                          </p>
                      }
                      </>
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tap to show answer */}
        {!isFlipped &&
        <div className="mt-6">
          <div className="flex flex-col justify-center">
            <div className="relative flex justify-center">
              <Button
                variant="default"
                size="sm"
                className="rounded-lg px-3"
                onClick={() => {
                  setIsFlipped(true);
                  setShowHint(false);
                }}>
                Tap to show answer
              </Button>
            </div>
          </div>
        </div>
        }
      </div>

      {/* Rating buttons */}
      {isFlipped &&
      <div className="mt-6">
          <div className="flex flex-col justify-center">
            <div className="mx-auto mt-0 flex w-fit flex-wrap justify-center gap-2 rounded-2xl border p-2">
              <Button
                variant="ghost"
                onClick={() => handleRate("again")}
                disabled={isRating}
                className="flex flex-col gap-1 border-none px-6 py-6 bg-red-50 text-red-500 hover:bg-red-50/70 hover:text-red-500 dark:bg-red-950 dark:text-red-400 dark:hover:bg-red-950/70 dark:hover:text-red-400">
                <span>Again</span>
                <span className="text-xs opacity-80">
                  {getNextReviewLabel("again", getSM2Fields())}
                </span>
              </Button>
              <Button
                variant="ghost"
                onClick={() => handleRate("hard")}
                disabled={isRating}
                className="flex flex-col gap-1 border-none px-6 py-6 bg-yellow-50 text-yellow-600 hover:bg-yellow-50/70 hover:text-yellow-600 dark:bg-yellow-950 dark:text-yellow-400 dark:hover:bg-yellow-950/70 dark:hover:text-yellow-400">
                <span>Hard</span>
                <span className="text-xs opacity-80">
                  {getNextReviewLabel("hard", getSM2Fields())}
                </span>
              </Button>
              <Button
                variant="ghost"
                onClick={() => handleRate("good")}
                disabled={isRating}
                className="flex flex-col gap-1 border-none px-6 py-6 bg-green-50 text-green-500 hover:bg-green-50/70 hover:text-green-500 dark:bg-green-950 dark:text-green-400 dark:hover:bg-green-950/70 dark:hover:text-green-400">
                <span>Good</span>
                <span className="text-xs opacity-80">
                  {getNextReviewLabel("good", getSM2Fields())}
                </span>
              </Button>
              <Button
                variant="ghost"
                onClick={() => handleRate("easy")}
                disabled={isRating}
                className="flex flex-col gap-1 border-none px-6 py-6 bg-blue-50 text-blue-500 hover:bg-blue-50/70 hover:text-blue-500 dark:bg-blue-950 dark:text-blue-400 dark:hover:bg-blue-950/70 dark:hover:text-blue-400">
                <span>Easy</span>
                <span className="text-xs opacity-80">
                  {getNextReviewLabel("easy", getSM2Fields())}
                </span>
              </Button>
            </div>
          </div>
        </div>
      }
    </div>);

}